import React, { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import type { SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { getSearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';
import { LoomFolderIcon } from '../../../../../quick-find/dialog/loom-folder-icon';
import { LoomPlaylistIcon } from '../../../../../quick-find/dialog/loom-playlist-icon';
import { LoomSpaceIcon } from '../../../../../quick-find/dialog/loom-space-icon';
import { LoomTagIcon } from '../../../../../quick-find/dialog/loom-tag-icon';

import type { searchLoomEntity_AtlassianSearchPage$key } from './__generated__/searchLoomEntity_AtlassianSearchPage.graphql';
import { messages } from './messages';

const styles = cssMap({
	iconContainer: {
		height: '32px',
		width: '32px',
	},
});

const LOOM_ENTITY_TYPENAMES = new Set([
	'SearchResultLoomFolder',
	'SearchResultLoomPlaylist',
	'SearchResultLoomSpace',
	'SearchResultLoomTag',
]);

export const isSearchLoomEntity = (node: { __typename?: string }): boolean =>
	!!node.__typename && LOOM_ENTITY_TYPENAMES.has(node.__typename);

const getIcon = (typename: string, title: string): React.JSX.Element => {
	let icon: React.JSX.Element;

	switch (typename) {
		case 'SearchResultLoomFolder':
			icon = <LoomFolderIcon />;
			break;
		case 'SearchResultLoomPlaylist':
			icon = <LoomPlaylistIcon />;
			break;
		case 'SearchResultLoomSpace':
			icon = <LoomSpaceIcon name={title} />;
			break;
		case 'SearchResultLoomTag':
			icon = <LoomTagIcon />;
			break;
		default:
			icon = <LoomFolderIcon />;
	}

	return <Box xcss={styles.iconContainer}>{icon}</Box>;
};

export const SearchLoomEntity = ({
	node,
	position,
	cloudId,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
	selectedSites,
}: SearchResultProps<searchLoomEntity_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchLoomEntity_AtlassianSearchPage on SearchResult {
				__typename
				id
				title
				type
				url
				... on SearchResultLoomFolder {
					totalNestedVideos
				}
				... on SearchResultLoomTag {
					count
				}
			}
		`,
		node,
	);
	const { formatMessage } = useIntl();
	const [, , , contentId] = useMemo(() => getProductAndIDFromARI(data.id), [data.id]);
	const metadata = useMemo(
		() =>
			({
				id: data.id,
				containerId: null,
				spaceId: null,
				contentType: data.type,
				entityType: data.type,
				contentId,
				product: ProductKeys.Loom,
				...getSiteMetadata(data.id, cloudId, selectedSites),
			}) satisfies AdvancedSearchResultInteractedMetadata,
		[cloudId, contentId, data.id, data.type, selectedSites],
	);

	const onResultClicked = useFireResultClicked({ metadata, position });
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({ metadata, position });
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();
	const videoCount =
		data.__typename === 'SearchResultLoomTag'
			? data.count
			: data.__typename === 'SearchResultLoomFolder'
				? data.totalNestedVideos
				: null;

	return (
		<SearchResult
			id={data.id}
			product={ProductKeys.Loom}
			productSelected={false}
			primaryProduct={primaryProduct}
			primarySubProduct={primarySubProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={data.url}
			title={enrichExcerpt(data.title)}
			icon={getIcon(data.__typename, data.title)}
			headerMetadataParts={[
				getSearchResultType(data.type as SearchClientResultType),
				[
					'video-count',
					videoCount != null ? formatMessage(messages.videoCount, { count: videoCount }) : null,
				],
			]}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
			selectedSites={selectedSites}
		/>
	);
};
